import { createFileRoute } from "@tanstack/react-router";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientHomePlanPublic } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationHydrated, ClientLocationWithLevel } from "api-types/locations/locationsResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import { ensureDefined } from "common/utils/assertOfType";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import CommunitiesTable from "lib/components/builders/communities/CommunitiesTable";
import NewLocationButton from "lib/components/builders/communities/NewLocationButton";
import PublicHomePlansTable from "lib/components/builders/communities/plans/homePlans/PublicHomePlansTable";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import { SubLocationNested } from "lib/components/builders/communities/plans/SubLocations";
import LocationSummaryView from "lib/components/builders/communities/summary/LocationSummaryView";
import {
  calculateExpansionMap,
  canExpandLocation,
  createDescendantMap,
  getLocationHomePlanCountWithDescendants,
  locationOrDescendantsMatchSearch,
} from "lib/components/builders/communities/utils/locationUtils";
import { getBuilderLocationsPaginatedURL } from "lib/endpointHelpers";
import { usePaginatedFetch } from "lib/fetcher";
import { errorPath, useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import Loading from "ui/components/Loading";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import withAuthentication from "../../../../components/withAuthentication";

type CommunitiesTabs = "locations" | "customer_plans" | "summary";

function CommunitiesPage() {
  const { builderId } = useAppRouterParams();

  if (!builderId) {
    return null;
  }

  return <ReadyCommunitiesPage builderId={builderId} />;
}

export function ReadyCommunitiesPage({ builderId }: { builderId: string }) {
  const navigate = useAppNavigate();
  const { user, builder, builderLoading, enabledFeatures, featuresLoaded, hasPermission } = useUserContext();
  const canViewLocationSummary = enabledFeatures.locationSummary && hasPermission("VIEW_LOCATION_SUMMARY");

  const visibleTabs: CommunitiesTabs[] = [];

  if (canViewLocationSummary) {
    visibleTabs.push("summary");
  }

  // Locations should be visible to anyone who can *view* communities
  if (hasPermission("VIEW_COMMUNITIES")) {
    visibleTabs.push("locations");
  }

  if (hasPermission("MOVE_CUSTOMER_PLAN")) {
    visibleTabs.push("customer_plans");
  }

  const { tab, menuItems } = useTabbedMenu<CommunitiesTabs>(visibleTabs);
  const summaryAccessDenied =
    !!user && !!builder && featuresLoaded && !builderLoading && tab === "summary" && !canViewLocationSummary;

  useEffect(() => {
    if (summaryAccessDenied) {
      void navigate({
        to: errorPath(403, undefined, "Your account is not allowed to access this resource."),
        replace: true,
      });
    }
  }, [navigate, summaryAccessDenied]);

  const newLocationButton: ReactNode = <NewLocationButton builderId={builderId} />;

  if (!user || !builder || summaryAccessDenied) {
    return null;
  }

  return (
    <BuilderPortalLayout
      header="Locations"
      headerActions={newLocationButton}
      selected="locations"
      pageTitle="Locations"
      menuItems={menuItems}
    >
      {tab === "locations" && <LocationsView builder={builder} newLocationButton={newLocationButton} />}
      {hasPermission("MOVE_CUSTOMER_PLAN") && tab === "customer_plans" && <CustomerPlans builderId={builderId} />}
      {canViewLocationSummary && tab === "summary" && (
        <div className="builder-portal-layout-content">
          <LocationSummaryView scope={{ kind: "builder", builderId }} />
        </div>
      )}
    </BuilderPortalLayout>
  );
}

function CustomerPlans({ builderId }: { builderId: string }) {
  const {
    data: homePlans,
    pagination: homePlanPagination,
    setPagination: setHomePlanPagination,
    loading: homePlanLoading,
    mutate: mutateHomePlans,
  } = usePaginatedFetch<ClientHomePlanPublic>(
    `${WEB_API_BASE_PATH}/builders/${builderId}/home-plans/public`,
    [builderId],
    {
      pageSize: 10,
    },
  );

  const handleHomePlanMove = (homePlanId: string) => {
    if (!homePlans) {
      return;
    }

    const newHomePlansData = homePlans.data.filter((hp) => hp.id !== homePlanId);

    void mutateHomePlans({ data: newHomePlansData, totalCount: homePlans.totalCount - 1 });
  };

  return (
    <div className="builder-portal-layout-content">
      <SectionHeader title="Customer Plans" subtitle="Home Plans created from Master Plan public links" hr />
      {homePlanLoading ? (
        <Loading />
      ) : (
        <PublicHomePlansTable
          homePlans={ensureDefined(homePlans).data}
          onDelete={handleHomePlanMove}
          totalCount={ensureDefined(homePlans).totalCount}
          pagination={homePlanPagination}
          setPagination={setHomePlanPagination}
        />
      )}
    </div>
  );
}

function LocationsView({ builder, newLocationButton }: { builder: ClientBuilder; newLocationButton: ReactNode }) {
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const {
    loading: locationsLoading,
    data: allLocations,
    pagination: locationsPagination,
    setPagination: setLocationsPagination,
    mutate,
  } = usePaginatedFetch<ClientLocationHydrated>(
    getBuilderLocationsPaginatedURL({
      builderId: builder.id,
      pagination: { orderBy: "depth", order: "asc" },
      includeArchived: showArchived,
    }),
    [builder.id],
    {
      // Large pageSize to get all locations at once because pagination breaks the location tree structure
      pageSize: 500,
    },
  );

  const paginatedLocations = useMemo(() => {
    if (!allLocations?.data) {
      return null;
    }

    const locationIds = new Set(allLocations.data.map((loc) => loc.id));
    // Show top-level locations plus authorized sub-locations whose parent is not visible.
    const rootLocations = allLocations.data
      .filter((loc) => !loc.parentId || !locationIds.has(loc.parentId))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      ...allLocations,
      data: rootLocations,
    };
  }, [allLocations]);

  const descendantsByParentLocation = useMemo(() => {
    return createDescendantMap(allLocations?.data ?? []);
  }, [allLocations]);

  const homePlanCountsWithDescendants = useMemo(() => {
    if (!allLocations?.data) {
      return {};
    }
    return getLocationHomePlanCountWithDescendants(allLocations.data);
  }, [allLocations]);

  const { visibleRootLocations, mustExpand } = useMemo(() => {
    if (!search) {
      return {
        visibleRootLocations: paginatedLocations?.data?.filter((loc) => showArchived || !loc.archivedAt) ?? [],
        mustExpand: {},
      };
    }

    const expand = calculateExpansionMap(allLocations?.data || [], search, descendantsByParentLocation, showArchived);

    // keep all top-level locations that have matches themselves or in children
    const visibleRootLocations = (paginatedLocations?.data ?? []).filter((loc) =>
      showArchived || !loc.archivedAt
        ? locationOrDescendantsMatchSearch(loc, search, descendantsByParentLocation, showArchived)
        : false,
    );

    return { visibleRootLocations, mustExpand: expand };
  }, [search, paginatedLocations?.data, descendantsByParentLocation, allLocations?.data, showArchived]);

  const canExpand = useCallback(
    (loc: ClientLocationHydrated) => {
      return canExpandLocation(loc, descendantsByParentLocation, search, showArchived);
    },
    [descendantsByParentLocation, search, showArchived],
  );

  if (locationsLoading || !paginatedLocations?.data || !visibleRootLocations) {
    return <Loading />;
  }

  const handleUpdateCommunity = async (community: ClientLocationWithLevel): Promise<void> => {
    if (!community || !paginatedLocations) {
      return;
    }

    const locations: ClientLocationWithLevel[] = paginatedLocations.data.map((c) =>
      c.id === community.id ? community : c,
    );

    await mutate({ ...paginatedLocations, data: locations });
  };

  return (
    <div className="builder-portal-layout-content">
      <CommunitiesTable
        locations={visibleRootLocations}
        search={search}
        setSearch={setSearch}
        forcedExpanded={mustExpand}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        pagination={{
          state: locationsPagination,
          totalCount: paginatedLocations.totalCount,
          setPagination: setLocationsPagination,
        }}
        onSuccess={handleUpdateCommunity}
        builder={builder}
        homePlanCountsWithDescendants={homePlanCountsWithDescendants}
        getRowCanExpand={(row) => canExpand(row.original)}
        subTable={(row) => (
          <SubLocationNested
            parentLocation={row.original}
            builderId={builder.id}
            locationId={row.original.id}
            search={search}
            setSearch={setSearch}
            forcedExpanded={mustExpand}
            showArchived={showArchived}
          />
        )}
        emptyCTA={
          <BuilderPortalEmptyCTA>
            <div className="cta-container">
              <div className="cta-title">Set up your first Location</div>
              <div className="cta-text">Once you add a location, you can add and configure plans.</div>
              {newLocationButton}
            </div>
          </BuilderPortalEmptyCTA>
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/builders/$builder_id/locations/")({
  component: withAuthentication(CommunitiesPage, { permissions: ["VIEW_COMMUNITIES"] }),
});
